import React, { useState, useEffect, useRef } from "react";
import {
  ActionIcon,
  Indicator,
  Paper,
  Box,
  Button,
  Text,
  Flex,
  Divider,
  Loader,
  Anchor,
} from "@mantine/core";
import { IconBellFilled } from "@tabler/icons-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { AlertList } from "@components/Alerts";
import { NotificationLink } from "@components/shared";
import { notifications } from "@mantine/notifications";
import axios from "axios";

export default function AlertWidget() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState("All");
  const dropdownRef = useRef(null);
  const adminInfo = useSelector((state) => state.admin);
  const managerInfo = useSelector((state) => state.manager);

  // Check if current page is /notifications
  const isAlertsPage = location.pathname === "/notifications";

  if (
    managerInfo &&
    !managerInfo.organization_manager_id &&
    !managerInfo.location_manager_id
  ) {
    return null;
  }

  // React Query for polling unread alerts count (runs continuously)
  const { data: showIndicator = false } = useQuery({
    queryKey: ["unreadAlertsCount"],
    queryFn: async () => {
      try {
        const response = await axios.post("/notifications/polling/", {
          organization_manager_id: managerInfo?.organization_manager_id,
          location_manager_id: managerInfo?.location_manager_id,
          admin_id: adminInfo?.admin_id,
        });
        const { data } = response;
        if (
          data.response &&
          data.response.length &&
          data.response[0].push_notifications &&
          data.response[0].push_notifications.length > 0
        ) {
          data.response[0].push_notifications.forEach((notification) => {
            notifications.show({
              id: notification.id,
              title: notification.title,
              message: notification.navigation_url ? (
                <NotificationLink
                  href={notification.navigation_url}
                  text={notification.message_text}
                  notificationId={notification.id}
                />
              ) : (
                notification.message_text
              ),
              color: "blue",
              autoClose: 20000,
            });
          });
        }
        return data.response[0].bell_dot;
      } catch (error) {
        return false;
      }
    },
    refetchInterval: 10 * 1000, // Poll every 10 seconds
    refetchIntervalInBackground: true, // Continue polling even when tab is not active
    staleTime: 0, // Always consider data stale to ensure fresh count
  });

  // React Query for fetching alerts (only when widget is open)
  const { data: alerts = [], isLoading: loading } = useQuery({
    queryKey: ["alertWidget", filter, isOpen],
    queryFn: async () => {
      try {
        // Make API call to fetch alerts
        const response = await axios.post("/notifications/widget-data/", {
          organization_manager_id: managerInfo?.organization_manager_id,
          location_manager_id: managerInfo?.location_manager_id,
          admin_id: adminInfo?.admin_id,
        });

        const { data } = response;

        // Return the alerts data from the response
        return data.response || [];
      } catch (error) {
        return [];
      }
    },
    enabled: isOpen, // Only fetch when widget is open
    staleTime: 0, // Always consider data stale to show loading on filter change
    cacheTime: 10 * 60 * 1000, // 10 minutes
    // refetchInterval: isOpen ? 10 * 1000 : false, // Only poll when open
    // refetchIntervalInBackground: isOpen, // Only poll in background when open
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    // Don't allow dropdown to open if on alerts page
    if (isAlertsPage) {
      return;
    }
    setIsOpen(!isOpen);
  };

  const handleSeeAll = () => {
    setIsOpen(false);
  };

  const handleAlertClick = (alert) => {
    setIsOpen(false);
    // Navigate to alerts page with the alert ID in state
    navigate("/notifications", {
      state: { widgetAlertId: alert.id, context: alert.context },
    });
  };

  return (
    <Box ref={dropdownRef} style={{ position: "relative" }}>
      <Indicator color="red" offset={5} size={10} disabled={!showIndicator}>
        <ActionIcon
          size={40}
          variant="light"
          radius="xl"
          onClick={handleToggle}
          style={{
            opacity: isAlertsPage ? 0.5 : 1,
            cursor: isAlertsPage ? "not-allowed" : "pointer",
          }}
        >
          <IconBellFilled />
        </ActionIcon>
      </Indicator>

      {isOpen && (
        <Paper
          shadow="xs"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            width: 600,
            zIndex: 1000,
            marginTop: 12,
            border: `1px solid var(--mantine-color-gray-3)`,
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <Box p="xs" pb={0}>
            <Flex gap="xs" align="center">
              <IconBellFilled
                size={24}
                style={{
                  color: "var(--mantine-color-blue-5)",
                }}
              />
              <Flex gap="xs" style={{ flexGrow: 1 }} align="center">
                <Flex align="center" gap="xs" style={{ flexGrow: 1 }}>
                  <Text size="lg" fw={600}>
                    Notifications
                  </Text>
                  {loading && <Loader size="xs" />}
                </Flex>
                <Anchor
                  size="xs"
                  c="dimmed"
                  onClick={() => {
                    navigate("/notifications");
                    setIsOpen(false);
                  }}
                >
                  Notification Center
                </Anchor>
              </Flex>
              {/* {unreadAlerts.length > 0 && (
                <Button
                  variant="subtle"
                  size="xs"
                  color="gray.5"
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as read
                </Button>
              )} */}
            </Flex>
            {/* <SegmentedControl
              data={["All", "Unread"]}
              value={filter}
              fullWidth
              mt="md"
              onChange={setFilter}
              radius="md"
            /> */}
          </Box>
          <Divider mt="md" />
          <AlertList
            maxHeight="500px"
            alerts={alerts}
            onAlertClick={handleAlertClick}
            showRead={false}
            isWidget
          />
          {alerts.length > 0 && (
            <Button
              variant="subtle"
              size="sm"
              fullWidth
              component={Link}
              to="/notifications"
              onClick={handleSeeAll}
            >
              See all
            </Button>
          )}
        </Paper>
      )}
    </Box>
  );
}
